import { useEffect, useState, type ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import {
  ChevronLeft,
  ChevronRight,
  Home,
  MessageSquare,
  History,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { ssoConfigured } from "@/config/sso";
import {
  getDisplayNameFromProfile,
  getStoredAccessToken,
  getUserDetails,
  getUserEmailFromProfile,
  logout,
} from "@/services/ssoApi";

const NAV_COLLAPSED_KEY = "beluga-nav-collapsed";

const NAV_ITEMS = [
  { path: "/voice", label: "Voice", icon: Home },
  { path: "/chatbot", label: "Chat", icon: MessageSquare },
  { path: "/conversation-history", label: "History", icon: History },
] as const;

function getInitialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

function readNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = ssoConfigured ? getStoredAccessToken() : null;
  const hasSsoToken = Boolean(token);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [profileEmail, setProfileEmail] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);

  useEffect(() => {
    if (!token) {
      setProfileName(null);
      setProfileEmail(null);
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    void (async () => {
      const details = await getUserDetails(token);
      if (cancelled) return;
      setProfileLoading(false);
      if (!details || (typeof details === "object" && "error" in details)) {
        setProfileName(null);
        setProfileEmail(null);
        return;
      }
      if (typeof details === "object" && !Array.isArray(details)) {
        const profile = details as Record<string, unknown>;
        const name = getDisplayNameFromProfile(profile);
        setProfileName(name.trim() || null);
        setProfileEmail(getUserEmailFromProfile(profile));
      } else {
        setProfileName(null);
        setProfileEmail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (location.pathname === "/") {
    return null;
  }

  const go = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const toggleNavCollapsed = () => {
    setNavCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const isNavActive = (path: string) => location.pathname === path;

  const navItemClass = (path: string) =>
    clsx(
      "flex w-full items-center rounded-md text-sm transition-colors",
      navCollapsed ? "md:justify-center md:p-2 md:px-0" : "px-2.5 py-1.5 text-left",
      isNavActive(path)
        ? "bg-[#EDF3FF] font-medium text-[#5B6EF9]"
        : "font-normal text-[#1A1A1A] hover:bg-gray-100",
    );

  const renderNavItem = (
    path: string,
    label: string,
    Icon: ComponentType<{ className?: string }>,
  ) => (
    <button
      key={path}
      type="button"
      onClick={() => go(path)}
      className={navItemClass(path)}
      aria-current={isNavActive(path) ? "page" : undefined}
      aria-label={label}
      title={label}
    >
      <Icon className={clsx("h-4 w-4 shrink-0", !navCollapsed && "mr-2", navCollapsed && "md:mr-0")} />
      <span className={clsx(navCollapsed && "md:hidden")}>{label}</span>
    </button>
  );

  const displayName = profileLoading && !profileName ? "…" : profileName || "Signed in";
  const initials =
    profileLoading && !profileName ? "…" : getInitialsFromName(profileName || "Signed in");

  const navPanel = (
    <div
      className={clsx(
        "relative w-max min-w-[200px] overflow-visible rounded-xl border border-gray-200 bg-white p-2 shadow-md transition-[width,padding]",
        navCollapsed && "md:w-max md:min-w-0 md:p-1.5",
      )}
      style={{
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 8px",
      }}
    >
      {hasSsoToken && (
        <div
          className={clsx(
            "relative mb-3 overflow-visible border-b border-gray-200 pb-2.5 pt-2",
            navCollapsed ? "md:mb-2 md:px-0 md:pb-2 md:pt-0" : "px-2.5",
          )}
        >
          <div
            className={clsx(
              "flex items-center",
              navCollapsed ? "gap-2.5 md:justify-center md:gap-1" : "gap-2.5",
            )}
            title={profileName ?? undefined}
          >
            <div
              className={clsx(
                "flex shrink-0 items-center justify-center rounded-full font-semibold text-white",
                navCollapsed ? "h-9 w-9 text-xs md:h-8 md:w-8" : "h-11 w-11 text-sm",
              )}
              style={{ backgroundColor: "#6D82E7" }}
              aria-hidden
            >
              {initials}
            </div>
            <div className={clsx("relative min-w-0 flex-1 pr-6", navCollapsed && "md:hidden")}>
              <span className="block text-[13px] font-semibold leading-snug text-[#1A1A1A]">
                {displayName}
              </span>
              {profileEmail && (
                <p className="mt-0.5 text-xs leading-snug text-gray-500">{profileEmail}</p>
              )}
              {!navCollapsed && (
                <button
                  type="button"
                  onClick={toggleNavCollapsed}
                  className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white p-0.5 text-gray-500 shadow-sm transition-colors hover:bg-gray-50 md:flex"
                  aria-label="Collapse navigation"
                  title="Collapse navigation"
                >
                  <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
          </div>
          {navCollapsed && (
            <button
              type="button"
              onClick={toggleNavCollapsed}
              className="absolute left-[30px] top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white p-0.5 text-gray-500 shadow-sm transition-colors hover:bg-gray-50 md:flex"
              aria-label="Expand navigation"
              title="Expand navigation"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      )}

      {!hasSsoToken && (
        <button
          type="button"
          onClick={toggleNavCollapsed}
          className={clsx(
            "absolute z-10 hidden items-center justify-center rounded-full border border-gray-200 bg-white p-0.5 text-gray-500 shadow-sm transition-colors hover:bg-gray-50 md:flex",
            navCollapsed ? "left-[30px] top-1/2 -translate-y-1/2" : "right-1 top-1",
          )}
          aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
          title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
        >
          {navCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      )}

      {NAV_ITEMS.map(({ path, label, icon }) => renderNavItem(path, label, icon))}

      {hasSsoToken && (
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            void logout();
          }}
          className={clsx(
            "flex w-full items-center rounded-md text-sm font-normal text-[#1A1A1A] transition-colors hover:bg-gray-100",
            navCollapsed ? "md:justify-center md:p-2 md:px-0" : "px-2.5 py-1.5 text-left",
          )}
          aria-label="Log out"
          title="Log out"
        >
          <LogOut className={clsx("h-4 w-4 shrink-0", !navCollapsed && "mr-2", navCollapsed && "md:mr-0")} />
          <span className={clsx(navCollapsed && "md:hidden")}>Log out</span>
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile: menu trigger (does not cover page content when closed) */}
      <button
        type="button"
        className={clsx(
          "pointer-events-auto fixed z-[200] flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white shadow-md md:hidden",
          "left-[max(0.75rem,env(safe-area-inset-left))] top-[max(0.75rem,env(safe-area-inset-top))]",
        )}
        aria-controls="app-primary-nav"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
        onClick={() => setMenuOpen((o) => !o)}
      >
        {menuOpen ? (
          <X className="h-5 w-5 text-gray-800" aria-hidden />
        ) : (
          <Menu className="h-5 w-5 text-gray-800" aria-hidden />
        )}
      </button>

      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[150] cursor-default border-0 bg-black/40 p-0 md:hidden"
          aria-label="Dismiss navigation menu"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <nav
        id="app-primary-nav"
        className={clsx(
          "pointer-events-auto fixed z-[160] flex max-h-[min(420px,calc(100dvh-5rem))] flex-col overscroll-contain",
          "max-md:overflow-y-auto md:overflow-visible",
          "left-[max(0.75rem,env(safe-area-inset-left))]",
          menuOpen ? "top-16 max-md:flex" : "max-md:hidden",
          "md:left-[max(1rem,env(safe-area-inset-left))] md:top-[max(1rem,env(safe-area-inset-top))] md:flex",
        )}
        style={{
          gap: "8px",
          alignItems: "flex-start",
        }}
      >
        {navPanel}
      </nav>
    </>
  );
}

export default Navigation;
